import { useCallback, useEffect, useMemo, useState } from "react";
import axiosInstance from "../../config/axiosConfig";
import ParcProfPdf from "./ParcProfPdf";
import { PDFDownloadLink } from "@react-pdf/renderer";
import {
  buildParcoursLabelMap,
  getParcoursDisplayName,
} from "../../utils/parcoursLabels";
import { extractApiErrorMessage } from "../../utils/apiErrorMessage";

function ParcProf(props) {
  const id = props.profId;
  const professeur = props.professeur;
  const userId = localStorage.getItem("userId");
  const userRole = localStorage.getItem("userRole");
  const semaine = localStorage.getItem("semaineStage");

  const [planningPayload, setPlanningPayload] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [actionMessage, setActionMessage] = useState("");
  const [submittingAction, setSubmittingAction] = useState(false);

  const buildPdfFileName = () => {
    const nom = professeur?.nom || "";
    const prenom = professeur?.prenom || "";
    const safeValue = `${nom}_${prenom}`
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "");

    return safeValue ? `planning_${safeValue}.pdf` : `planning_${id}.pdf`;
  };

  const loadPlanning = useCallback(() => {
    if (!semaine) {
      setPlanningPayload(null);
      setErrorMessage(
        "Choisissez d'abord une semaine de planning depuis l'espace admin pour afficher ce planning."
      );
      return;
    }

    axiosInstance
      .get(`/planningweeks/${semaine}/encadrants/${id}/planning`)
      .then((response) => {
        setPlanningPayload(response.data);
        setErrorMessage("");
      })
      .catch((error) => {
        console.error(error);
        setPlanningPayload(null);
        setErrorMessage(
          extractApiErrorMessage(
            error,
            "Le planning de cet encadrant n'a pas pu etre charge."
          )
        );
      });
  }, [id, semaine]);

  useEffect(() => {
    loadPlanning();
  }, [loadPlanning]);

  const canConfirmOwnPlanning =
    (userRole === "Encadrant" || userRole === "Encadrant et Tuteur") &&
    String(userId) === String(id);

  const parcoursLabelMap = useMemo(() => {
    const parcoursIds = [];

    Object.values(planningPayload?.moments || {}).forEach((moment) => {
      (moment.items || []).forEach((item) => {
        if (item.parcoursId) {
          parcoursIds.push(item.parcoursId);
        }
      });
    });

    return buildParcoursLabelMap(parcoursIds);
  }, [planningPayload]);

  const orderedMoments = useMemo(() => {
    return Object.values(planningPayload?.moments || {})
      .filter((moment) => (moment.items || []).length > 0)
      .sort((leftMoment, rightMoment) => leftMoment.indexMoment - rightMoment.indexMoment);
  }, [planningPayload]);

  const handleConfirmPlanning = () => {
    setSubmittingAction(true);
    setActionMessage("");
    setErrorMessage("");

    axiosInstance
      .put(`/planningweeks/${semaine}/encadrants/confirmations/me`)
      .then((response) => {
        setPlanningPayload(response.data);
        setActionMessage(
          response.data?.message || "Votre planning a bien ete confirme."
        );
      })
      .catch((error) => {
        console.error(error);
        setErrorMessage(
          extractApiErrorMessage(
            error,
            "La confirmation de votre planning n'a pas pu etre finalisee."
          )
        );
      })
      .finally(() => {
        setSubmittingAction(false);
      });
  };

  const handleClearConfirmation = () => {
    setSubmittingAction(true);
    setActionMessage("");
    setErrorMessage("");

    axiosInstance
      .delete(`/planningweeks/${semaine}/encadrants/confirmations/me`)
      .then((response) => {
        setPlanningPayload(response.data);
        setActionMessage(
          response.data?.message || "Votre confirmation a bien ete retiree."
        );
      })
      .catch((error) => {
        console.error(error);
        setErrorMessage(
          extractApiErrorMessage(
            error,
            "La suppression de votre confirmation n'a pas pu etre finalisee."
          )
        );
      })
      .finally(() => {
        setSubmittingAction(false);
      });
  };

  return (
    <div className="parcours-prof-content">
      <div className="parcours-prof-header">
        <p className="parcours-prof-text">
          Retrouvez ici uniquement votre planning de la semaine selectionnee.
        </p>
        {semaine && (
          <p className="parcours-prof-text">
            Semaine suivie : <strong>{semaine}</strong>
          </p>
        )}
      </div>

      {planningPayload && (
        <section className="parcours-prof-status-card">
          <div>
            <p className="parcours-prof-status-tag">
              {planningPayload.isConfirmed ? "Planning confirme" : "Confirmation en attente"}
            </p>
            <h3>Suivi de votre semaine</h3>
            <p className="parcours-prof-text">
              {planningPayload.isConfirmed
                ? "Votre planning est bien confirme. L'admin peut maintenant suivre votre validation."
                : "Relisez votre planning de la semaine, puis confirmez-le pour indiquer a l'admin que tout est bon de votre cote."}
            </p>
          </div>

          {canConfirmOwnPlanning && (
            <div className="convention-actions-row">
              {!planningPayload.isConfirmed ? (
                <button
                  className="btn"
                  type="button"
                  disabled={submittingAction}
                  onClick={handleConfirmPlanning}
                >
                  {submittingAction ? "Confirmation..." : "Confirmer mon planning"}
                </button>
              ) : (
                <button
                  className="btn btn-secondary"
                  type="button"
                  disabled={submittingAction}
                  onClick={handleClearConfirmation}
                >
                  {submittingAction ? "Mise a jour..." : "Retirer ma confirmation"}
                </button>
              )}
            </div>
          )}
        </section>
      )}

      {actionMessage && (
        <p className="eleve-feedback is-success">{actionMessage}</p>
      )}

      {errorMessage && (
        <p className="eleve-feedback is-error">{errorMessage}</p>
      )}

      {planningPayload && orderedMoments.length > 0 ? (
        <div className="parcours-prof-grid is-schedule">
          {orderedMoments.map((moment) => (
            <section className="parcours-prof-column" key={moment.indexMoment}>
              <h3 className="parcours-prof-column-title">{moment.momentLabel}</h3>
              <div className="parcours-prof-column-list">
                {moment.items.map((item) => (
                  <article className="parcours-prof-planning-card" key={`${item.activiteId}-${item.parcoursId}-${item.indexMoment}`}>
                    <p className="parcours-prof-badge">
                      {getParcoursDisplayName(item.parcoursId, parcoursLabelMap)}
                    </p>
                    <h4>{item.activite?.nom || `Activite ${item.activiteId}`}</h4>
                    {item.activite?.lieu && (
                      <p>Lieu : {item.activite.lieu}</p>
                    )}
                    {item.activite?.lieu_rdv && (
                      <p>Rendez-vous : {item.activite.lieu_rdv}</p>
                    )}
                  </article>
                ))}
              </div>
            </section>
          ))}
        </div>
      ) : planningPayload && (
        <div className="internal-empty">
          Aucune activite ne vous est encore attribuee sur cette semaine.
        </div>
      )}

      {planningPayload && orderedMoments.length > 0 && (
        <div className="parcours-prof-footer">
          <PDFDownloadLink
            className="link parcours-prof-download"
            document={
              <ParcProfPdf
                moments={planningPayload.moments}
                nom={professeur.nom}
                prenom={professeur.prenom}
              />
            }
            fileName={buildPdfFileName()}
          >
            {({ loading }) =>
              loading ? "Téléchargement en cours..." : "Télécharger mon planning"
            }
          </PDFDownloadLink>
        </div>
      )}
    </div>
  );
}

export default ParcProf;
